<template>

  <div class="page page-profile">
    <div class="container">
    <transition name="slide-fade">

      <div v-if="user">

        <transition name="slide-fade">
            <div v-if="localError" class="alert alert-danger" role="alert">
              {{localError}}
            </div>
        </transition>

        <header>
        <h1 class="page-header">My Research Profile</h1>

        <div class="page-description">
          Completing <span v-if="user.guardian">this</span><span v-else>your</span> profile is important. We use this information to manage your kits and to keep you up to date with important information. In addition to the research project/s you are participating in, here you can also take the opportunity to join Microba's Future Insights Program to help build a deeper understanding of the human microbiome and make a meaningful contribution to new discoveries. Some fields are mandatory, so please read the information carefully.
        </div>
        </header>

          <div class="box box-shadow">

            <h3 v-if="user.guardian">Please complete your child or dependant's details</h3>
            <h3 v-else>Personal details</h3>

          <div class="row">

            <div class="form-group col col-md-3">
              <b-form-input
                type="text"
                class=""
                v-model="user.firstName"
                required
                autocomplete="given-name"
                v-validate="'required'"
                maxlength="25"
                name="firstName"
                :state="errors.has('firstName') ? false : null"
                placeholder="First name"></b-form-input>
                <div class="invalid-feedback" v-show="errors.has('firstName')">{{ errors.first('firstName') }}</div>
            </div>

            <div class="form-group col col-md-3">
              <b-form-input
                type="text"
                class=""
                v-model="user.lastName"
                autocomplete="family-name"
                required
                v-validate="'required'"
                maxlength="25"
                name="lastName"
                :state="errors.has('lastName') ? false : null"
                placeholder="Last name"></b-form-input>
                <div class="invalid-feedback" v-show="errors.has('lastName')">{{ errors.first('lastName') }}</div>
            </div>

            <div class="form-group-select col-6 col-md-3">
              <vSelect
                placeholder="Gender"
                name="Gender"
                v-model="user.gender"
                v-validate="'required'"
                :class="{'is-invalid': errors.has('Gender')}"
                :options="['MALE', 'FEMALE', 'OTHER']"
                autocomplete="gender"
                label="label"
                :state="errors.has('Gender') ? false : null">
              </vSelect>
              <div class="text-danger" v-show="errors.has('Gender')">{{ errors.first('Gender') }}</div>
            </div>

            <div class="form-group-select col-6 col-md-3">
              <date-picker
                style="width: 100%"
                name="Year of birth"
                v-model="user.dateOfBirth"
                v-validate="'required'"
                placeholder="Select Year of Birth"
                type="year"
                :not-before="oneHundredTwentyYearsBefore"
                :not-after="new Date()"
                :first-day-of-week="1"
                lang="en"
                format="YYYY"
                input-class="form-control">
              </date-picker>
              <span class="text-danger" v-show="errors.has('Year of birth')">{{ errors.first('Year of birth') }}</span>
            </div>


            <div class="form-group col-md-6">
              <b-form-input
                class=""
                type="email"
                v-model="user.email"
                v-validate="'required|email'"
                autocomplete="email"
                maxlength="25"
                name="email"
                :state="errors.has('email') ? false : null"
                required
                :disabled="true"
                placeholder="Enter email"></b-form-input>
                <div class="invalid-feedback" v-show="errors.has('email')">{{ errors.first('email') }}</div>
            </div>

            <div class="form-group col-md-6">
              <b-form-input
                class=""
                type="tel"
                v-model="user.phoneNumber"
                maxlength="15"
                autocomplete="tel"
                required
                v-validate="'required'"
                name="phone"
                :state="errors.has('phone') ? false : null"
                placeholder="Phone number"></b-form-input>
                <div class="invalid-feedback" v-show="errors.has('phone')">{{ errors.first('phone') }}</div>
            </div>

          </div><!--.row -->

        </div><!--.box -->



        <div class="box box-shadow">

            <h3>Shipping Address</h3>

            <div class="row">

              <div class="form-group col-12 col-md-12" :class="{ 'has-danger' : errors.has('address1') }">
                <b-form-input
                  type="text"
                  v-model="user.addressLine1"
                  autocomplete="address-line1"
                  required
                  name="address1"
                  class="fs-hide"
                  v-validate="'required'"
                  :state="errors.has('address1') ? false : null"
                  placeholder="Address (required)"></b-form-input>
                <div class="invalid-feedback" v-show="errors.has('address1')">{{ errors.first('address1') }}</div>

              </div>

              <div class="form-group col-12 col-md-12">
                <b-form-input
                  type="text"
                  class="fs-hide"
                  autocomplete="address-line2"
                  v-model="user.addressLine2"
                  placeholder="Address 2"></b-form-input>
              </div>

              <div class="form-group col-12 col-md-4">
                <b-form-input
                  type="text"
                  v-model="user.suburb"
                  required
                  class="fs-hide"
                  name="suburb"
                  v-validate="'required'"
                  autocomplete="locality"
                  :state="errors.has('suburb') ? false : null"
                  placeholder="Suburb (required)"></b-form-input>
                  <div class="invalid-feedback" v-show="errors.has('suburb')">{{ errors.first('suburb') }}</div>
              </div>

              <div class="form-group-select col-12 col-md-4">
                <vSelect
                  placeholder="State (required)"
                  name="state"
                  :class="{'is-invalid': errors.has('state')}"
                  :options="['ACT', 'NSW', 'NT', 'SA', 'TAS', 'QLD', 'VIC', 'WA']"
                  v-model="user.state"
                  autocomplete="shipping region"
                  label="label"
                  v-validate="'required'"
                  :state="errors.has('state') ? false : null">
                </vSelect>
                <div class="invalid-feedback" v-show="errors.has('state')">{{ errors.first('state') }}</div>
              </div>

              <div class="form-group col col-md-4">
                <b-form-input
                  type="text"
                  v-model="user.postcode"
                  required
                  name="postcode"
                  autocomplete="shipping postal-code"
                  :state="errors.has('postcode') ? false : null"
                  v-validate="'required|digits:4'"
                  placeholder="Postcode (required)"></b-form-input>
                  <div class="invalid-feedback" v-show="errors.has('postcode')">{{ errors.first('postcode') }}</div>
              </div>

          </div><!--.row -->

        </div><!--.box -->

        <div class="box box-shadow box-password-reset" :class="{ 'box-collapse': collapsePassword }">

            <div v-if="collapsePassword" v-b-toggle.resetPasswordBox class="box-header row">
              <div class="col-8">
              <h3>I would like to change my password</h3>
              </div>
              <div class="col-4">
                <div class="box-toggle-icon change-password-toggle">
                  <i  class="icon-chevron-bottom"></i>
                </div>
                <!--<div class="box-toggle-icon btn btn-outline-light change-password-toggle">Change password</div>-->
              </div>
            </div>



          <b-collapse :visible="!collapsePassword"  id="resetPasswordBox" class="box-main">

            <h3>Change Password</h3>

            <div class="form-row">
              <div class="form-group col-12 col-md-7">
                <b-form-input
                  type="password"
                  autocomplete="off"
                  autocorrect="off"
                  autocapitalize="off"
                  spellcheck="false"
                  v-model="currentPassword"
                  placeholder="Current password"></b-form-input>
              </div>

              <div class="form-group col-12 col-md-7">
                <b-form-input
                  type="password"
                  v-model="newPassword"
                  autocomplete="off"
                  autocorrect="off"
                  autocapitalize="off"
                  spellcheck="false"
                  placeholder="New password"></b-form-input>
              </div>

              <div class="input-group col-12 col-md-7">
                <b-form-input
                  type="password"
                  autocomplete="off"
                  autocorrect="off"
                  autocapitalize="off"
                  spellcheck="false"
                  v-model="newPasswordConfirm"
                  :is-invalid="!validPassword"
                  placeholder="Confirm new password"></b-form-input>

                <div class="invalid-feedback">
                    <span v-if="!validPassword">Passwords do not match</span>
                  </div>
              </div>

        </div><!--.box -->

        </b-collapse>
      </div>

        <ProfileParentalConsent v-if="user.underAge" :parentalConsent="user.parentConsented" @onSuccess="onParentalSuccess()" @onSkipConsent="skipMinorConsentToggle" :localError="parentError"/>

        <div class="concent-flow box box-shadow">
          <consentFlow v-if="consentFlow && !user.underAge" :consentFlow="consentFlow" :activeConsentId="activeConsentId" v-on:fetch-consent-flow="loadNextConsent" v-on:consent-response="saveConsentResponse"/>
        </div>

      <div class="row">
        <div class="col-12">
          <div class="text-center">
            <Button text="Save changes" :saving="loading" :success="buttonSuccess" :error="error" @onClick="onClickSave" />
          </div>
        </div>
      </div>
      </div> <!-- if-user -->
    </transition>

    <Loader v-if="loading" />
    </div>
  </div>
</template>

<script>

import Loader from '@/components/elements/Loader'
import Button from '@/components/elements/DynamicButton'
import ProfileParentalConsent from '@/components/elements/ProfileParentalConsent'
import userProfile from '@/mixins/userProfile'
import vSelect from 'vue-select'
import DatePicker from 'vue2-datepicker'
import moment from 'moment'
import consentFlow from '@microbadevs/consent-flow'
import httpClientService from '@/services/httpClientService'
import {microbaLoader, microbaSuccess, microbaFailure, microbaWarning} from '@microbadevs/notifications'

export default {
  name: 'Profile',
  mixins: [userProfile],
  components: {
    Loader,
    Button,
    ProfileParentalConsent,
    vSelect,
    DatePicker,
    consentFlow
  },
  mounted () {
    this.$store.dispatch('getUser')
    .then(() => {
      this.getConsentFlow(process.env.CONSENT_ID)
    })
  },
  data () {
    return {
      currentPassword: null,
      newPassword: null,
      newPasswordConfirm: null,
      buttonSuccess: false,
      collapsePassword: true,
      localError: null,
      parentError: null,
      originalUser: null, // Used to check if user is opting out/in
      skipMinorConsent: false,
      consentFlowResponse: null,
      consentFlow: null,
      activeConsentId: null,
      consentResponse: null
    }
  },
  watch: {
    saving: function (val) {
      if (this.success === true) {
        this.buttonSuccess = true
        this.$bus.$emit('enableSidebar')
        setTimeout(() => {
          this.buttonSuccess = false
        }, 2000)
      }
    },
    user: function () {
      if (this.originalUser === null) {
        this.originalUser = this.user
      }
    }
  },
  computed: {
    user: function () {
      return this.$store.state.GET_USER_RESPONSE
    },
    loading: function () {
      return this.$store.state.GET_USER_PENDINGvalidatedUser
    },
    saving: function () {
      return this.$store.state.PUT_USER_PENDING
    },
    success: function () {
      return this.$store.state.PUT_USER_SUCCESS
    },
    error: function () {
      return this.$store.state.PUT_USER_ERROR
    },
    validatedUser: function () {
      const newUser = this.user
      if (this.validPassword && this.currentPassword && this.newPassword) {
        newUser.password = this.currentPassword
        newUser.newPassword = this.newPassword
      }
      newUser.userType = 'RESEARCH'
      return newUser
    },
    validPassword: function () {
      return this.newPassword === this.newPasswordConfirm
    },
    // if the dashboard.profileCompleted was false we assume they were redirected here so on save we redirect back to the dashboard
    wasInvalid: function () {
      return (this.$store.state.GET_DASHBOARD_RESPONSE) ? this.$store.state.GET_DASHBOARD_RESPONSE.profileCompleted === false : true
    },
    dashboard: function () {
      return this.$store.state.GET_DASHBOARD_RESPONSE
    },
    oneHundredTwentyYearsBefore: function () {
      return moment().subtract(120, 'years').calendar()
    }
  },
  methods: {
    skipMinorConsentToggle () {
      this.skipMinorConsent = !this.skipMinorConsent
    },
    onParentalSuccess: function () {
      this.user.parentConsented = null
      this.parentError = null
    },
    onClickSave: function () {
      this.localError = null
      this.parentError = null

      this.$validator.validate().then(() => {
        if (this.errors.any()) {
          this.localError = 'Please fill out or correct the highlighted fields'
          scroll(0, 0)
          return
        }

        if (this.user.underAge === true && this.user.parentConsented === false && !this.skipMinorConsent) {
          this.parentError = 'Please supply a signed parental consent form'
          return
        }

        // On save, if use has never agreed to data usage, present modal
        if (this.user.agreeToDataUsage === null && this.consentResponse === null && this.user.underAge === false) {
          microbaWarning({text: 'Please provide consent for Microba Future Insight Program.'})
          return
        } else {
          this.doSave()
        }
      })
    },
    saveUser: function () {
      this.$store.dispatch('putUser', this.validatedUser).then(success => {
        microbaSuccess({text: 'Saved successfully.'})
        this.buttonSuccess = true
        if (this.wasInvalid === true) {
          this.$store.dispatch('getDashboard').then(success => {
            if (this.dashboard.numberOfUnassignedKits < 1) {
              this.$router.push('/dashboard?showModal=REDEEM')
            } else {
              this.$router.push('/dashboard')
            }
          })
        }
      })
    },
    doSave: function (event) {
      this.buttonSuccess = false
      microbaLoader.showLoader()
      if (this.consentResponse) {
        httpClientService.getAPIHttpClient().post(`/consent/${this.consentResponse.consentId}`, this.consentResponse)
        .then(() => {
          this.saveUser()
        })
        .catch(() => {
          microbaFailure({text: 'Unable to save consent response, please contact support.'})
        })
      } else {
        this.saveUser()
      }
    },
    loadNextConsent (payload) {
      this.getConsentFlow(payload.consentFlowId, payload.nextConsentId)
    },
    saveConsentResponse (payload) {
      const consentData = {
        consentId: payload.id,
        consenterType: 'USER',
        consenterId: this.user.id,
        consented: payload.userResponse
      }
      this.consentResponse = consentData
    },
    getConsentFlow (consentId, activeConsentId = null) {
      microbaLoader.showLoader()
      if (this.user.id) {
        httpClientService.getAPIHttpClient().get(`/consent-flow/${consentId}?consenter-type=USER&consenter-id=${this.user.id}`)
      .then((response) => {
        this.consentFlow = response.data
        this.activeConsentId = activeConsentId
      })
      .catch(error => {
        console.log(error)
      })
      .finally(() => {
        if (activeConsentId) {
          setTimeout(() => {
            microbaLoader.hideLoader()
          }, 1000)
        } else {
          microbaLoader.hideLoader()
        }
      })
      }
    }
  }
}
</script>

<style scoped>
  h1 {
    margin-bottom: 40px;
  }

  img{
    border-radius: 99em;
    margin: 0px auto 0;
    display: block;
    background: red;
    box-shadow: 0 9px 16px rgba(0,0,0,0.15);
    border: 4px solid white;
  }

  .input-group {
    margin-bottom: 1.2rem;
  }
   .invalid-feedback{
    display: block !important;
  }
  .change-password-toggle{
    float: right;
  }
  .box-password-reset{
    padding-top: 1.5rem;
  }
  .box-password-reset h3{
    margin: 0;
    line-height: 42px;
  }
  .box-main {
    padding-top: 2rem;
  }


  .change-password-toggle{
    margin-top: 12px;
  }


</style>
<style lang="scss">
.consent-flow{
  .heading{
    .title{
      font-family:athelas;
      font-size: 21px;
      font-weight: 500;
      line-height:42px;
    }
    .toggle-icon{
      display: inline-block;
      vertical-align: middle;
      font-size: 16px;
      margin: 0 0 1rem 0;
      font-weight: 500;
      color: #00193d;
    }
    .toggle-icon{
      font-family: 'icomoon' !important;
      margin-top: 2em;
    }
    .new-consent-icon{
      color: #ff8708;
      font-size: 16px;
      margin-right: 6px;
    }
    .new-consent-btn{
      font-size: 16px;
      font-weight: 500;
      color: #ff8708;
      cursor: pointer;
      text-transform: uppercase;
      &:hover{
        color: #ff8708;
      }
    }
  }

  .show{
    .simple-consent{
      .content{
        p{
          font-size: 16px;
        }
      }

          .radio-buttons .opt-in {
      border: 1px solid #ff8708;
      padding: 1rem 2rem;
      border-radius: 99em;
      label {
        margin: 0 auto;
        color: #ff8708;
      }
    }

    .opt-out {
      // border: 1px solid #ff8708;
      padding: 1rem 2rem;
      border-radius: 99em;
      label {
        font-size: 0.94rem;
        margin: 0 auto;
        color: #ff8708;
      }
    }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/application/research/Profile.vue